import { trim } from 'lodash';
import { isAnyOf } from '@reduxjs/toolkit';
import {
  signInWithEmailAndPassword, EmailAuthProvider, linkWithCredential, createUserWithEmailAndPassword,
} from 'firebase/auth';
import { batch } from 'react-redux';
import { trackAnalytics } from '@client/intercom';
import { showError } from '@client/listeners/lib/alerts';
import { firebaseRequest, handleRequest } from '@client/listeners/lib/requests';
import { showLoginErrorSnackbar, showSnackbar } from '@client/listeners/lib/snackbar';
import { setLoaderIsLoading, setLoaderIsFinishedSuccess, setLoaderIsFinishedError } from '@client/slices/helpers/loaders.reducer';
import {
  setCurrentSessionUser, startEmailLogin, startEmailSignup, startSendForgotPasswordEmail,
} from '@client/slices/helpers/session.reducer';
import { createUser } from '@client/listeners/utils/session';
import type { EmailSignupPayload, SigninPayload } from '@sda/interfaces/reducer/session.reducer';
import type { AddListener } from '@client/listeners/lib';

export const signedOutListeners = (addListener: AddListener) => [
  addListener({
    actionCreator: startSendForgotPasswordEmail,
    effect: async (action, listenerApi) => {
      const { email, id } = action.payload;

      const { dispatch } = listenerApi;

      const scope = 'forgot-password-button';
      dispatch(setLoaderIsLoading(scope));

      try {
        const request = await firebaseRequest({
          url: `${process.env.API_BASE_URL}/system/forgotPassword`,
          method: 'POST',
          body: {
            id,
            email: trim(email),
          },
        });

        const response = await handleRequest(request);

        if (response.status) {
          dispatch(setLoaderIsFinishedSuccess(scope));
          showSnackbar('A password reset email has been sent to you');
        } else {
          dispatch(setLoaderIsFinishedError(scope, response.error));
        }

        trackAnalytics(startSendForgotPasswordEmail);
      } catch (e) {
        if (e.message === 'Not Found') {
          dispatch(setLoaderIsFinishedSuccess(scope));
        } else {
          showError(e);
          dispatch(setLoaderIsFinishedError(scope, e));
        }
      }
    },
  }),

  addListener({
    matcher: isAnyOf(startEmailLogin, startEmailSignup),
    effect: async (action, listenerApi) => {
      const { intent = null, ...credentials } = action.payload as EmailSignupPayload & SigninPayload;

      const { dispatch } = listenerApi;
      const firebase = window.Firebase;

      const scope = 'email-password-form';
      dispatch(setLoaderIsLoading(scope));

      try {
        const { currentUser } = firebase.auth;
        const isAnonymous = currentUser && currentUser.isAnonymous;

        const { email, password, acceptsTos = false } = credentials;

        if (startEmailLogin.match(action)) {
          if (currentUser) {
            await firebase.auth.signOut();
          }

          await signInWithEmailAndPassword(firebase.auth, trim(email), password);

          trackAnalytics(startEmailLogin, { email });
        } else if (currentUser && isAnonymous) {
          const emailCredential = EmailAuthProvider.credential(trim(email), password);
          const { user: newUser } = await linkWithCredential(firebase.auth.currentUser, emailCredential);

          await createUser({
            ...newUser, ...credentials, acceptsTos, intent,
          }, listenerApi);

          trackAnalytics(startEmailSignup, { email });
        } else {
          const { user: newUser } = await createUserWithEmailAndPassword(firebase.auth, trim(email), password);

          await createUser({
            ...newUser, ...credentials, acceptsTos, intent,
          }, listenerApi);

          trackAnalytics(startEmailSignup, { email });
        }

        dispatch(setLoaderIsFinishedSuccess('email-password-form'));
      } catch (e) {
        showLoginErrorSnackbar(e);

        batch(() => {
          dispatch(setCurrentSessionUser(false));
          dispatch(setLoaderIsFinishedError('email-password-form', e));
        });
      }
    },
  }),
];
